export const defaultProjectPythonVersion = "3.12"

export const projectJSRuntimeVersion = 1

export const projectJSRuntimeDependencies = {
	react: "19.2.4",
	"react-dom": "19.2.4",
	three: "0.182.0",
} as const

export type ProjectJSDependency = { name: string; version: string }

export const projectJSDependencyNamePattern =
	/^(@[A-Za-z0-9][A-Za-z0-9._-]*\/)?[A-Za-z0-9][A-Za-z0-9._-]*$/

export const projectJSExactVersionPattern =
	/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/

export const validateProjectJSDependencies = (
	dependencies: ProjectJSDependency[],
) => {
	const names = new Set<string>()
	for (const dependency of dependencies) {
		if (!projectJSDependencyNamePattern.test(dependency.name)) {
			throw new Error(`Invalid npm package name: ${dependency.name}`)
		}
		if (!projectJSExactVersionPattern.test(dependency.version)) {
			throw new Error(
				`npm package versions must be exact: ${dependency.version}`,
			)
		}
		const normalized = dependency.name.toLowerCase()
		if (names.has(normalized)) {
			throw new Error(`Duplicate npm package: ${dependency.name}`)
		}
		names.add(normalized)
		if (Object.hasOwn(projectJSRuntimeDependencies, normalized)) {
			throw new Error(
				`${dependency.name} is provided by the React node runtime`,
			)
		}
	}
}

export const projectJSPackageManifest = (
	projectID: string,
	dependencies: ProjectJSDependency[],
) => {
	validateProjectJSDependencies(dependencies)
	const userDependencies = Object.fromEntries(
		[...dependencies]
			.sort((left, right) => left.name.localeCompare(right.name))
			.map(({ name, version }) => [name, version]),
	)
	const normalizedID =
		projectID
			.toLowerCase()
			.replaceAll(/[^a-z0-9-]/g, "-")
			.slice(0, 48) || "runtime"
	return `${JSON.stringify(
		{
			name: `oblique-project-${normalizedID}`,
			private: true,
			type: "module",
			version: "0.0.0",
			dependencies: {
				...projectJSRuntimeDependencies,
				...userDependencies,
			},
		},
		null,
		2,
	)}\n`
}

export const projectJSLockHash = async (
	lock: string,
	runtimeVersion = projectJSRuntimeVersion,
) => {
	const bytes = new TextEncoder().encode(`${runtimeVersion}\0${lock}`)
	const digest = await crypto.subtle.digest("SHA-256", bytes)
	return Array.from(new Uint8Array(digest), (byte) =>
		byte.toString(16).padStart(2, "0"),
	).join("")
}

export const projectNodeTypes = ["python", "react"] as const

export type ProjectNodeType = (typeof projectNodeTypes)[number]

export type ProjectJSONValue =
	| null
	| boolean
	| number
	| string
	| ProjectJSONValue[]
	| { [key: string]: ProjectJSONValue }

export type ProjectNodeInput = {
	type?: string
	required?: boolean
	description?: string
	value?: ProjectJSONValue
}

export type ProjectNodeInputs = Record<string, ProjectNodeInput>

export type ProjectNodeEdge = {
	sourceHandle?: string
	sourceNodeID: string
	targetHandle?: string
	targetNodeID: string
}

export const pythonIdentifierPattern = /^[A-Za-z_][A-Za-z0-9_]*$/

type SentenceParameterSpan = {
	end: number
	name: string
	start: number
}

const sentenceParameterOccurrences = (
	functionName: string,
	parameterName: string,
) => {
	const occurrences: SentenceParameterSpan[] = []
	let cursor = 0
	for (;;) {
		const start = functionName.indexOf(parameterName, cursor)
		if (start === -1) return occurrences
		const end = start + parameterName.length
		const startsOnBoundary = start === 0 || functionName[start - 1] === "_"
		const endsOnBoundary =
			end === functionName.length || functionName[end] === "_"
		if (startsOnBoundary && endsOnBoundary) {
			occurrences.push({ end, name: parameterName, start })
		}
		cursor = start + 1
	}
}

const humanizeSentenceText = (value: string) =>
	value.replaceAll(/_+/g, " ").replaceAll(/\s+/g, " ").trim()

export const deriveProjectNodeSentence = (
	functionName: string,
	parameterNames: string[],
) => {
	const spans = parameterNames.map((parameterName) => {
		const occurrences = sentenceParameterOccurrences(
			functionName,
			parameterName,
		)
		if (occurrences.length === 0) {
			throw new Error(
				`Function name must contain parameter '${parameterName}' exactly once`,
			)
		}
		if (occurrences.length > 1) {
			throw new Error(
				`Function name contains parameter '${parameterName}' more than once`,
			)
		}
		return occurrences[0]!
	})

	spans.sort((left, right) => left.start - right.start)
	for (let index = 1; index < spans.length; index += 1) {
		const previous = spans[index - 1]!
		const current = spans[index]!
		if (current.start < previous.end) {
			throw new Error(
				`Function name has overlapping parameter placements: '${previous.name}' and '${current.name}'`,
			)
		}
	}

	let cursor = 0
	let template = ""
	for (const span of spans) {
		template += functionName.slice(cursor, span.start)
		template += `{${span.name}}`
		cursor = span.end
	}
	template += functionName.slice(cursor)
	const placeholders: string[] = []
	template = template.replaceAll(/\{[^{}]+\}/g, (placeholder) => {
		const index = placeholders.push(placeholder) - 1
		return `\uE000${index}\uE001`
	})
	template = humanizeSentenceText(template).replaceAll(
		/\uE000(\d+)\uE001/g,
		(_token, index: string) => placeholders[Number(index)] ?? "",
	)
	return template ? template[0]!.toUpperCase() + template.slice(1) : template
}

export const validateProjectNodeSentence = (
	sentence: string,
	parameterNames: string[],
) => {
	const template = sentence.trim()
	if (!template) throw new Error("Node sentence is required")

	const counts = new Map(parameterNames.map((name) => [name, 0]))
	const placeholderPattern = /\{([^{}]+)\}/g
	for (const match of template.matchAll(placeholderPattern)) {
		const name = match[1] ?? ""
		if (!counts.has(name)) {
			throw new Error(`Node sentence contains unknown input '${name}'`)
		}
		counts.set(name, (counts.get(name) ?? 0) + 1)
	}
	if (template.replaceAll(placeholderPattern, "").match(/[{}]/)) {
		throw new Error("Node sentence contains an invalid placeholder")
	}
	for (const [name, count] of counts) {
		if (count === 0) {
			throw new Error(
				`Node sentence must contain input '{${name}}' exactly once`,
			)
		}
		if (count > 1) {
			throw new Error(`Node sentence contains input '{${name}}' more than once`)
		}
	}
	return template
}

export const isProjectJSONValue = (
	value: unknown,
): value is ProjectJSONValue => {
	if (value === null) return true
	if (typeof value === "boolean" || typeof value === "string") return true
	if (typeof value === "number") return Number.isFinite(value)
	if (Array.isArray(value)) return value.every(isProjectJSONValue)
	if (typeof value !== "object") return false
	return Object.values(value).every(isProjectJSONValue)
}

export const setProjectNodeInputValue = (
	inputs: ProjectNodeInputs,
	inputID: string,
	value: ProjectJSONValue,
) => {
	const input = inputs[inputID]
	if (!input) throw new Error(`Input ${inputID} not found`)
	return { ...inputs, [inputID]: { ...input, value } }
}

export const clearProjectNodeInputValue = (
	inputs: ProjectNodeInputs,
	inputID: string,
) => {
	const input = inputs[inputID]
	if (!input) throw new Error(`Input ${inputID} not found`)
	const { value: _value, ...definition } = input
	return { ...inputs, [inputID]: definition }
}

export const resolveProjectNodeInputValues = (
	nodeID: string,
	inputs: ProjectNodeInputs,
	edges: ProjectNodeEdge[],
	outputs: ReadonlyMap<string, unknown>,
) => {
	const values: Record<string, unknown> = Object.fromEntries(
		Object.entries(inputs).flatMap(([name, input]) =>
			"value" in input ? [[name, input.value]] : [],
		),
	)
	for (const edge of edges) {
		if (edge.targetNodeID !== nodeID || !edge.targetHandle) continue
		if (!outputs.has(edge.sourceNodeID)) continue
		const output = outputs.get(edge.sourceNodeID)
		const edgeOutput =
			edge.sourceHandle &&
			output !== null &&
			typeof output === "object" &&
			edge.sourceHandle in output
				? (output as Record<string, unknown>)[edge.sourceHandle]
				: output
		values[edge.targetHandle] = edgeOutput
	}
	return values
}

export const parseProjectNodeOutput = (serialized: string) => {
	try {
		return JSON.parse(serialized) as unknown
	} catch {
		return serialized
	}
}
